import { Box, Container, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useHandler } from "@redotech/react-util/hook";
import { LoadState, useLoad, useTriggerLoad } from "@redotech/react-util/load";

import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { removeGidFromId } from "@redotech/redo-customer-portal-shared/util";
import { DraftReturn } from "@redotech/redo-model/draft-return/draft-return";
import {
  getCombinedReturnItem,
  getCompensationMethod,
  getPrimaryProduct,
} from "@redotech/redo-model/draft-return/util";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Modal } from "@redotech/redo-web/modal";
import { Spinner, SpinnerStyle } from "@redotech/redo-web/spinner";
import { RpcError } from "@redotech/rpc/errors";
import { ProductSortKeys } from "@redotech/shopify-client/storefront.graphql";
import {
  ShopifyStorefrontClient,
  getProductRecommendations,
  getProducts,
} from "@redotech/shopify-storefront";
import {
  ShopifyIdType,
  convertShopifyIdToGid,
} from "@redotech/util/shopify-id";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { getProductInfo } from "../../util/util";
import { ExchangeContext } from "../new-items";
import * as advancedExchange from "./advanced-exchange.module.css";
import { CartMenu } from "./cart-menu";
import { ExchangeItem } from "./exchange-item";
import { Footer } from "./footer";
import { ItemDetailsModal } from "./item-details-modal";
import * as page from "./page.module.css";
import { SearchBar } from "./search-bar";
import { Selector } from "./selector";
import { ExchangeProduct, ExchangeProductVariant } from "./variant-bubbles";

export const AdvancedExchange = memo(function AdvancedExchange({
  exchangeContext,
  settings,
  draftReturn,
  setDraftReturn,
  client,
  merchantDashboard,
  onComplete,
}: {
  exchangeContext: ExchangeContext;
  settings: ReturnAppSettings;
  draftReturn: DraftReturn;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  client: CustomerPortalRpcClient;
  merchantDashboard?: boolean;
  onComplete: () => void;
}) {
  const [itemDetailsModalOpen, setItemDetailsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ExchangeProduct | null>(null);
  const [selectedVariant, setSelectedVariant] =
    useState<ExchangeProductVariant | null>(null);
  const [cartMenuOpen, setCartMenuOpen] = useState(false);
  const [showingSimilarItems, setShowingSimilarItems] = useState(false);
  const [searchedItems, setSearchedItems] = useState<ExchangeProduct[]>([]);
  const [displayingSearchResults, setDisplayingSearchResults] = useState(false);
  const [cantAddInfoOpen, setCantAddInfoOpen] = useState(false);
  const itemToRemoveId = useRef<string | null>(null);
  const { formatCurrency } = useContext(CurrencyContext);

  const clearSearchResults = () => {
    setDisplayingSearchResults(false);
    setSearchedItems([]);
  };

  const [addNewItemLoad, addNewItem] = useTriggerLoad(async (signal) => {
    if (selectedVariant == null) {
      return;
    }
    try {
      const response = await client.addNewItems({
        draftReturnId: draftReturn._id,
        items: [
          {
            variantId: String(selectedVariant.id),
            productId: String(selectedVariant.productId),
            quantity: 1,
            properties: {},
          },
        ],
      });

      setDraftReturn(response.draftReturn);
      setItemDetailsModalOpen(false);
      setCartMenuOpen(true);
    } catch (e) {
      if (
        (e as RpcError<string>).message ===
        "Total price of new items exceeds the total return value"
      ) {
        setCantAddInfoOpen(true);
        return;
      }
    }
  });

  const [_removeNewItemLoad, removeNewItem] = useTriggerLoad(async (signal) => {
    if (itemToRemoveId.current == null) {
      return;
    }
    const response = await client.removeNewItems({
      draftReturnId: draftReturn._id,
      variantIds: [itemToRemoveId.current],
    });

    setDraftReturn(response.draftReturn);
    setItemDetailsModalOpen(false);
  });

  const onItemAdd = (variant: ExchangeProductVariant | null) => {
    if (variant == null) {
      return;
    }
    setItemDetailsModalOpen(false);
    setSelectedVariant(variant);
    addNewItem();
  };

  const onItemRemove = (id: string) => {
    setCartMenuOpen(false);
    itemToRemoveId.current = id;
    removeNewItem();
  };

  const storefrontClient = new ShopifyStorefrontClient(
    settings.storeUrl,
    settings.storefrontAccessToken,
  );

  const excludedProductTags = settings?.exchanges?.excludeProductTags.map(
    (tag: string) => tag.toLowerCase(),
  );
  const productsLoad = useLoad(async () => {
    const allProducts: ExchangeProduct[] = [];

    const iterator = getProducts(storefrontClient, {
      first: 250,

      query: `-vendor:re\\:do`,
      sortKey: ProductSortKeys.BestSelling,
    });
    for await (const product of iterator) {
      allProducts.push(getProductInfo(product));
      if (
        allProducts.length >= 250 &&
        !settings?.loadAllAdvancedExchangeProducts
      ) {
        break;
      }
    }
    return allProducts.filter(
      (product) =>
        product.vendor !== "re:do" &&
        product.availableForSale &&
        // Hide virtual products
        product.variants.some(
          (variant) =>
            variant.requiresShipping &&
            !product.tags.some((tag: string) =>
              excludedProductTags.includes(tag.toLowerCase()),
            ),
        ),
    );
  }, []);

  const similarProductsLoad = useLoad(async () => {
    const response = await getProductRecommendations(
      storefrontClient,
      convertShopifyIdToGid(
        getPrimaryProduct(
          getCombinedReturnItem(
            draftReturn,
            draftReturn?.pendingReturnItems?.[0]?.id,
          ).returnableItem,
        ).productId,
        ShopifyIdType.Product,
      ),
    );

    const similarProducts = response.map(getProductInfo).filter(
      (product) =>
        product.availableForSale &&
        // Hide virtual products
        product.variants.some(
          (variant) =>
            variant.requiresShipping &&
            !product.tags.some((tag: string) =>
              excludedProductTags.includes(tag.toLowerCase()),
            ),
        ),
    );
    if (similarProducts.length > 0) {
      setShowingSimilarItems(true);
    }
    return similarProducts;
  }, []);

  const selectedProductLoad = useLoad(async () => {
    if (!exchangeContext.selectedProduct?.id) {
      return undefined;
    }

    const iterator = getProducts(storefrontClient, {
      first: 1,

      query: `id:${removeGidFromId(exchangeContext.selectedProduct?.id)}`,
    });
    for await (const product of iterator) {
      return getProductInfo(product);
    }

    return undefined;
  }, [exchangeContext.selectedProduct]);

  useEffect(() => {
    if (!selectedProductLoad.value) {
      return;
    }
    setSelectedProduct(selectedProductLoad.value);
    setItemDetailsModalOpen(true);
  }, [selectedProductLoad.value]);

  return (
    <div>
      {cantAddInfoOpen && (
        <Snackbar
          autoHideDuration={6000}
          onClose={() => setCantAddInfoOpen(false)}
          open={cantAddInfoOpen}
        >
          <Alert onClose={() => setCantAddInfoOpen(false)} severity="info">
            {`Cart total cannot be greater than value of item(s) being returned. (${formatCurrency(
              getCompensationMethod(
                draftReturn,
              )?.totalReturnableValue?.toNumber() || 0,
            )}).`}
          </Alert>
        </Snackbar>
      )}
      <CartMenu
        client={client}
        draftReturn={draftReturn}
        handleContinue={() => {
          onComplete();
        }}
        merchantDashboard={merchantDashboard}
        onItemRemove={onItemRemove}
        open={cartMenuOpen}
        setOpen={setCartMenuOpen}
        settings={settings}
      />
      <Modal
        onClose={() => setItemDetailsModalOpen(false)}
        open={itemDetailsModalOpen}
        title=""
      >
        <ItemDetailsModal
          onItemSelected={onItemAdd}
          onItemSelectedLoad={addNewItemLoad}
          open={itemDetailsModalOpen}
          selectedProduct={selectedProduct}
          setOpen={setItemDetailsModalOpen}
          settings={settings}
        />
      </Modal>
      <div className={advancedExchange.page}>
        <div className={advancedExchange.scrollable}>
          <div className={advancedExchange.header}>
            <Typography className={advancedExchange.headerText} variant="h4">
              {merchantDashboard ? "Edit Exchange Order" : "Choose New Items"}
            </Typography>
          </div>
          {merchantDashboard && (
            <div
              className={advancedExchange.cartContainer}
              onClick={() => {
                setCartMenuOpen(true);
              }}
            >
              <div className={page.cartSizeBubble}>
                {draftReturn?.newOrderItems.length || 0}
              </div>
              <svg
                className={page.cart}
                fill="none"
                height="24"
                stroke="#000000"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                viewBox="0 0 24 24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="20.5" r="1" />
                <circle cx="18" cy="20.5" r="1" />
                <path d="M2.5 2.5h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6l1.6-8.4H7.1" />
              </svg>
            </div>
          )}

          {!merchantDashboard && (
            <div className={advancedExchange.selector}>
              <Selector
                clearSearchResults={clearSearchResults}
                setShowingSimilarItems={setShowingSimilarItems}
                showingSimilarItems={showingSimilarItems}
              />
            </div>
          )}
          <ExchangeItemsList
            addItemLoad={addNewItemLoad}
            clearSearchResults={clearSearchResults}
            displayingSearchResults={displayingSearchResults}
            draftReturn={draftReturn}
            excludedProductTags={excludedProductTags}
            productsLoad={productsLoad}
            searchedItems={searchedItems}
            selectedProduct={selectedProduct}
            setDisplayingSearchResults={setDisplayingSearchResults}
            setItemDetailsModalOpen={setItemDetailsModalOpen}
            setSearchedItemsState={setSearchedItems}
            setSelectedProduct={setSelectedProduct}
            setShowingSimilarItems={setShowingSimilarItems}
            settings={settings}
            showingSimilarItems={showingSimilarItems}
            similarProductsLoad={similarProductsLoad}
          />
        </div>
        {!merchantDashboard && (
          <div className={advancedExchange.footer}>
            <div className={advancedExchange.footerContent}>
              <Footer
                buttonDisabled={draftReturn?.newOrderItems?.length < 1}
                buttonPending={false}
                buttonText="Continue"
                draftReturn={draftReturn}
                handleClick={() => {
                  onComplete();
                }}
                hideBranding={settings?.hidePortalBranding}
                returnType={draftReturn.returnType}
                showTooltip={false}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const ExchangeItemsList = memo(function ExchangeItemsList({
  settings,
  showingSimilarItems,
  setShowingSimilarItems,
  clearSearchResults,
  setItemDetailsModalOpen,
  searchedItems,
  setSearchedItemsState,
  displayingSearchResults,
  setDisplayingSearchResults,
  selectedProduct,
  setSelectedProduct,
  productsLoad,
  similarProductsLoad,
  excludedProductTags,
  addItemLoad,
}: {
  settings: ReturnAppSettings;
  showingSimilarItems: boolean;
  setShowingSimilarItems: (showingSimilarItems: boolean) => void;
  clearSearchResults: () => void;
  setItemDetailsModalOpen: (open: boolean) => void;
  draftReturn: DraftReturn;
  searchedItems: ExchangeProduct[];
  setSearchedItemsState: (items: ExchangeProduct[]) => void;
  displayingSearchResults: boolean;
  setDisplayingSearchResults: (displayingSearchResults: boolean) => void;
  selectedProduct: ExchangeProduct | null;
  setSelectedProduct: (product: ExchangeProduct | null) => void;
  productsLoad: LoadState<ExchangeProduct[]>;
  similarProductsLoad: LoadState<ExchangeProduct[]>;
  excludedProductTags: string[];
  addItemLoad: LoadState<void | undefined>;
}) {
  const [searchString, setSearchString] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const setSearchedItems = useHandler((items: ExchangeProduct[]) => {
    setSearchedItemsState(items);
    if (showingSimilarItems) {
      setShowingSimilarItems(false);
    }
  });

  const filterSearchProduct = (product: ExchangeProduct): boolean => {
    return !product.tags.some((tag: string) =>
      excludedProductTags.includes(tag.toLowerCase()),
    );
  };

  if (!productsLoad?.value || !similarProductsLoad?.value) {
    return <LoadingRedoAnimation />;
  }

  return (
    <Container maxWidth="lg">
      <Grid container spacing={2}>
        <Grid item lg={9.5} md={9} sm={8} xs={6}>
          {displayingSearchResults ? (
            <p className={advancedExchange.resultsText}>
              {searchedItems.length} Results
            </p>
          ) : null}
        </Grid>
        <Grid item lg={2.2} md={2.5} sm={3.5} xs={4.25}>
          <Stack direction="row" spacing={1}>
            <SearchBar
              filterSearchProduct={filterSearchProduct}
              getProductInfo={getProductInfo}
              searchQuery={searchQuery}
              setDisplayingSearchResults={setDisplayingSearchResults}
              setSearchedItems={setSearchedItems}
              setSearchLoading={setSearchLoading}
              setSearchQuery={setSearchQuery}
              setSearchString={setSearchString}
              settings={settings}
            />
          </Stack>
        </Grid>
      </Grid>
      {displayingSearchResults && (
        <Stack
          className={advancedExchange.searchQueryText}
          direction="row"
          spacing={0}
        >
          <Typography variant="h6">
            You searched for "{searchString}"
          </Typography>
          <Chip
            className={advancedExchange.exitButton}
            label=""
            onDelete={clearSearchResults}
          />
        </Stack>
      )}

      <div
        className={advancedExchange.container}
        style={
          displayingSearchResults ? { display: "none" } : { display: "inherit" }
        }
      >
        <Grid container spacing={0}>
          {!showingSimilarItems ? (
            <>
              <Box
                className={advancedExchange.circularProgressContainer}
                sx={{
                  display: productsLoad.pending ? "inherit" : "none!important",
                }}
              >
                <div className={advancedExchange.spinnerContainer}>
                  <Spinner style={SpinnerStyle.DARK} />
                </div>
              </Box>
              {productsLoad.value.map((product) => (
                <Grid
                  className={advancedExchange.exchangeItemGrid}
                  item
                  key={product.id}
                  lg={2.4}
                  md={3}
                  sm={4}
                  xs={6}
                >
                  <ExchangeItem
                    chooseButtonPending={
                      addItemLoad?.pending && product.id === selectedProduct?.id
                    }
                    product={product}
                    setItemDetailsModalOpen={setItemDetailsModalOpen}
                    setSelectedProduct={setSelectedProduct}
                  />
                </Grid>
              ))}
            </>
          ) : (
            <>
              <Box
                className={advancedExchange.circularProgressContainer}
                sx={{
                  display: similarProductsLoad.pending
                    ? "inherit"
                    : "none!important",
                }}
              >
                <div className={advancedExchange.spinnerContainer}>
                  <Spinner style={SpinnerStyle.DARK} />
                </div>
              </Box>
              {!similarProductsLoad.pending &&
                similarProductsLoad.value.length === 0 && (
                  <Typography
                    className={advancedExchange.noResultsText}
                    variant="h5"
                  >
                    No results found
                  </Typography>
                )}
              {similarProductsLoad.value.map((product) => (
                <Grid
                  className={advancedExchange.exchangeItemGrid}
                  item
                  key={product.id}
                  lg={2.4}
                  md={3}
                  sm={4}
                  xs={6}
                >
                  <ExchangeItem
                    chooseButtonPending={
                      addItemLoad?.pending && product.id === selectedProduct?.id
                    }
                    product={product}
                    setItemDetailsModalOpen={setItemDetailsModalOpen}
                    setSelectedProduct={setSelectedProduct}
                  />
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </div>
      <div
        className={advancedExchange.container}
        style={
          displayingSearchResults ? { display: "inherit" } : { display: "none" }
        }
      >
        <Grid container spacing={0}>
          <Box
            className={advancedExchange.circularProgressContainer}
            sx={{ display: searchLoading ? "inherit" : "none!important" }}
          >
            <div className={advancedExchange.spinnerContainer}>
              <Spinner style={SpinnerStyle.DARK} />
            </div>
          </Box>
          {!searchLoading && searchedItems.length === 0 && (
            <Typography className={advancedExchange.noResultsText} variant="h5">
              No results found
            </Typography>
          )}
          {searchedItems.map((product) => (
            <Grid
              className={advancedExchange.exchangeItemGrid}
              item
              key={product.id}
              lg={2.4}
              md={3}
              sm={4}
              xs={6}
            >
              <ExchangeItem
                chooseButtonPending={
                  addItemLoad?.pending && product.id === selectedProduct?.id
                }
                product={product}
                setItemDetailsModalOpen={setItemDetailsModalOpen}
                setSelectedProduct={setSelectedProduct}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    </Container>
  );
});
